import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useInternals } from '../../context/internalContext';
import { useAuxiliaries } from '../../context/auxiliariesContext';
import { usePenalExecution } from '../../context/penalExecutionContext';
import Modal from '../../components/modal';
import TooltipInfo from '../../components/TooltipInfo';
import { toast } from 'react-toastify';
import useSubmitLock from '../../utils/useSubmitLock';

// Estado inicial do formulário
const initialFormData = (internalId) => ({
  number: '',
  court: '',
  court_id: '',
  judicial_district: '',
  criminal_capitulation: [],
  nature_of_prison: '',
  prison_data: '',
  origin: '',
  years: '',
  months: '',
  days: '',
  regime: '',
  execution: '',
  extended: false,
  is_active: true,
  file: null,
  description: '',
  type: [],
  internal: internalId || ''
});

const normalizeText = (value) => {
  if (!value) return '';
  return value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[ºª]/g, '')
    .toUpperCase();
};

const normalizeSearchText = (value) => {
  if (!value) return '';
  return value
    .toString()
    .trim()
    .replace(/\s+/g, ' ');
};

const getCriminalCapitulationLabel = (item) => {
  const article = String(item?.capitulation || item?.art || item?.article || '').trim();
  const typology = normalizeText(
    item?.typology
    || item?.tipology
    || item?.common_name
    || item?.tipificacao
    || item?.typification
    || ''
  ).trim();

  if (article && typology) return `ART. ${article} - ${typology}`;
  if (article) return `ART. ${article}`;
  return typology;
};

function Process({ internalId, processId, isOpen, closeModal, onSaveSuccess, readOnly }) {
  const {
    addProcess,
    updateProcess,
    getProcessById,
    listInternalRegime,
  } = useInternals();
  const {
    listOrigins,
    listNatureOfPrisons,
    listCourts,
    listCriminalCapitulations,
    listTypeFiles,
  } = useAuxiliaries();
  const {
    listPenalExecutions,
  } = usePenalExecution();

  // Estados agrupados por finalidade
  const [formData, setFormData] = useState(initialFormData(internalId));
  const [searchState, setSearchState] = useState({
    searchCriminalCapitulation: ''
  });
  const [processSearchUi, setProcessSearchUi] = useState({
    showCourtSuggestions: false,
    showDistrictSuggestions: false,
  });
  const [loadingState, setLoadingState] = useState({
    judicialDistricts: false,
    courts: false,
    criminalCapitulation: false,
    penalExecutions: false,
    regimes: false,
    processData: false,
  });
  const [lists, setLists] = useState({
    origins: [],
    naturesOfPrison: [],
    districtResults: [],
    courtResults: [],
    criminalCapitulationResults: [],
    fileTypes: [],
    penalExecutions: [],
    regimes: [],
  });

  const originalProcessRef = useRef(null);
  const { isSubmitting, runWithSubmitLock } = useSubmitLock();

  const selectedNature = lists.naturesOfPrison.find(
    (n) => String(n.id) === String(formData.nature_of_prison || '')
  );
  const selectedNatureName = (selectedNature?.name || '').toLowerCase();
  const isPreventiva = selectedNatureName.includes('preventiva');
  const isCondenatoria = selectedNatureName.includes('conden');
  const isTemporaria = selectedNatureName.includes('tempor');
  const isCivil = selectedNatureName.includes('civil');

  const filteredDistrictSuggestions = useMemo(() => {
    return [...new Set(
      (Array.isArray(lists.districtResults) ? lists.districtResults : [])
        .map((item) => item?.district || item?.comarca || item?.judicial_district || item?.name || item)
        .filter(Boolean)
        .map((item) => String(item))
    )].sort((a, b) => a.localeCompare(b, 'pt-BR')).slice(0, 40);
  }, [lists.districtResults]);

  const filteredCourtSuggestions = useMemo(() => {
    return [...new Set(
      (Array.isArray(lists.courtResults) ? lists.courtResults : [])
        .map((item) => item?.unit || item?.unidade || item?.court || item?.vara || item?.name || item)
        .filter(Boolean)
        .map((item) => String(item))
    )].sort((a, b) => a.localeCompare(b, 'pt-BR')).slice(0, 40);
  }, [lists.courtResults]);

  const isEditMode = !!processId;

  //  * Limpa o formulário ao abrir o modal para novo cadastro
  useEffect(() => {
    if (!isEditMode && isOpen) {
      setFormData(initialFormData(internalId));
    }
  }, [isOpen, isEditMode, internalId]);

  //  * Busca dados do processo para edição, e listas auxiliares
  useEffect(() => {
    let isMounted = true;
    // Busca dados do processo se edição
    async function fetchProcessData() {
      try {
        setLoadingState(prev => ({ ...prev, processData: true }));

        const process = await getProcessById(processId);
        let criminalCapitulationFull = [];
        if (process.criminal_capitulation?.length > 0) {
          if (Array.isArray(process.criminal_capitulation)) {
            criminalCapitulationFull = process.criminal_capitulation
              .map((item) => {
                if (typeof item === 'string' || typeof item === 'number') {
                  return { id: '', label: String(item) };
                }
                const label = getCriminalCapitulationLabel(item);
                return label ? { id: item?.id || '', label } : null;
              })
              .filter(Boolean);
          } else if (typeof process.criminal_capitulation === 'string') {
            criminalCapitulationFull = process.criminal_capitulation
              .split(',')
              .map((s) => s.trim())
              .map((item) => ({ id: '', label: item }))
              .filter(Boolean);
          }
        }
        if (isMounted) {
          originalProcessRef.current = {
            number: process.number || '',
            court: process.court || '',
            court_id: process.court?.id ?? process.court_id ?? '',
            judicial_district: process.judicial_district || '',
            criminal_capitulation: criminalCapitulationFull,
            nature_of_prison: process.nature_of_prison?.id ?? process.nature_of_prison ?? '',
            origin: process.origin?.id ?? process.origin ?? '',
            prison_data: process.prison_data ? process.prison_data.split('T')[0] : '',
            years: process.years || '',
            months: process.months || '',
            days: process.days || '',
            regime: process.regime?.id ?? process.regime ?? '',
            execution: process.execution || '',
            extended: !!process.extended,
            is_active: process.is_active !== false,
          };
          setFormData({
            number: process.number || '',
            court: process.court?.unit || process.court?.name || process.court || '',
            court_id: process.court?.id ?? process.court_id ?? '',
            judicial_district: process.judicial_district || '',
            criminal_capitulation: criminalCapitulationFull,
            nature_of_prison: process.nature_of_prison?.id ?? process.nature_of_prison ?? '',
            origin: process.origin?.id ?? process.origin ?? '',
            prison_data: process.prison_data ? process.prison_data.split('T')[0] : '',
            years: process.years || '',
            months: process.months || '',
            days: process.days || '',
            regime: process.regime?.id ?? process.regime ?? '', execution: process.execution || '',
            extended: !!process.extended,
            is_active: process.is_active !== false,
            file: null,
            description: '',
            type: [],
            internal: internalId || ''
          });
        }
      } catch (error) {
        console.error("Erro ao buscar dados do processo:", error);
      } finally {
        if (isMounted) {
          setLoadingState(prev => ({ ...prev, processData: false }));
        }
      }
    }

    // Busca listas auxiliares
    async function fetchAuxiliaryData() {
      try {
        setLoadingState((prev) => ({ ...prev, regimes: true }));
        if (!isEditMode) {
           setLoadingState(prev => ({ ...prev, processData: false }));
        }
        const [o, n, t, r] = await Promise.all([
          listOrigins(),
          listNatureOfPrisons(),
          listTypeFiles(),
          listInternalRegime(internalId)
        ]);

        if (isMounted) {
          setLists(prev => ({
            ...prev,
            origins: Array.isArray(o?.results) ? o.results : (Array.isArray(o) ? o : []),
            naturesOfPrison: Array.isArray(n?.results) ? n.results : (Array.isArray(n) ? n : []),
            fileTypes: Array.isArray(t?.results) ? t.results : (Array.isArray(t) ? t : []),
            regimes: Array.isArray(r?.results) ? r.results : (Array.isArray(r) ? r : []),
          }));
          setLoadingState((prev) => ({ ...prev, regimes: false }));
        }
      }
      finally {
        if (isMounted) {
          setLoadingState((prev) => ({ ...prev, regimes: false }));
        }
      }
    }
    if (isEditMode) {
      fetchProcessData();
    } else {
      setFormData(initialFormData(internalId));
    }
    fetchAuxiliaryData();
    return () => { isMounted = false; };
  }, [
    isEditMode,
    processId,
    getProcessById,
    internalId,
    listOrigins,
    listNatureOfPrisons,
    listTypeFiles,
    listInternalRegime,
  ]);

  // Busca execuções penais do interno
  useEffect(() => {
    let isMounted = true;

    async function fetchPenalExecutions() {
      try {
        setLoadingState((prev) => ({ ...prev, penalExecutions: true }));
        const results = await listPenalExecutions({
          internal: internalId,
          is_active: true,
          limit: 100,
        });
        if (!isMounted) return;
        setLists((prev) => ({
          ...prev,
          penalExecutions: Array.isArray(results?.results) ? results.results : (Array.isArray(results) ? results : []),
        }));
        setLoadingState((prev) => ({ ...prev, penalExecutions: false }));
      } catch (error) {
        setLoadingState((prev) => ({ ...prev, penalExecutions: false }));
      }
    }

    if (internalId) {
      fetchPenalExecutions();
    }

    return () => { isMounted = false; };
  }, [internalId, listPenalExecutions, isOpen]);

  useEffect(() => {
    let isMounted = true;
    const search = normalizeSearchText(formData.judicial_district);

    if (!processSearchUi.showDistrictSuggestions || search.length < 2) {
      setLists((prev) => ({ ...prev, districtResults: [] }));
      setLoadingState((prev) => ({ ...prev, judicialDistricts: false }));
      return;
    }

    const debounce = setTimeout(async () => {
      setLoadingState((prev) => ({ ...prev, judicialDistricts: true }));
      const results = await listCourts({
        district: search,
        ordering: 'district',
        limit: 100,
      });
      if (!isMounted) return;
      setLists((prev) => ({
        ...prev,
        districtResults: Array.isArray(results) ? results : [],
      }));
      setLoadingState((prev) => ({ ...prev, judicialDistricts: false }));
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(debounce);
      setLoadingState((prev) => ({ ...prev, judicialDistricts: false }));
    };
  }, [formData.judicial_district, processSearchUi.showDistrictSuggestions, listCourts]);

  useEffect(() => {
    let isMounted = true;
    const district = normalizeSearchText(formData.judicial_district);
    const unit = normalizeSearchText(formData.court);

    if (!processSearchUi.showCourtSuggestions || !district) {
      setLists((prev) => ({ ...prev, courtResults: [] }));
      setLoadingState((prev) => ({ ...prev, courts: false }));
      return;
    }

    const debounce = setTimeout(async () => {
      setLoadingState((prev) => ({ ...prev, courts: true }));
      const results = await listCourts({
        district,
        ...(unit ? { unit } : {}),
        ordering: 'unit',
        limit: 100,
      });
      if (!isMounted) return;
      setLists((prev) => ({
        ...prev,
        courtResults: Array.isArray(results) ? results : [],
      }));
      setLoadingState((prev) => ({ ...prev, courts: false }));
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(debounce);
      setLoadingState((prev) => ({ ...prev, courts: false }));
    };
  }, [formData.judicial_district, formData.court, processSearchUi.showCourtSuggestions, listCourts]);

  useEffect(() => {
    let isMounted = true;
    const search = String(searchState.searchCriminalCapitulation || '').trim();

    if (search.length < 2) {
      setLists((prev) => ({ ...prev, criminalCapitulationResults: [] }));
      setLoadingState((prev) => ({ ...prev, criminalCapitulation: false }));
      return;
    }

    const debounce = setTimeout(async () => {
      setLoadingState((prev) => ({ ...prev, criminalCapitulation: true }));
      const results = await listCriminalCapitulations(search);
      if (!isMounted) return;
      setLists((prev) => ({
        ...prev,
        criminalCapitulationResults: Array.isArray(results) ? results.slice(0, 40) : [],
      }));
      setLoadingState((prev) => ({ ...prev, criminalCapitulation: false }));
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(debounce);
      setLoadingState((prev) => ({ ...prev, criminalCapitulation: false }));
    };
  }, [searchState.searchCriminalCapitulation, listCriminalCapitulations]);

  //  * Handler para campos do formulário (inclui upload de arquivo)
  const handleChange = useCallback(async (e) => {
    const { name, value, files, type, checked } = e.target;

    if (name === 'file') {
      const nextFile = files && files.length > 0 ? files[0] : null;

      setFormData(prev => ({
        ...prev,
        file: nextFile,
        description: nextFile ? prev.description : '',
        type: nextFile ? prev.type : [],
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  const handleFileTypeToggle = useCallback((typeId) => {
    const value = String(typeId);

    setFormData((prev) => ({
      ...prev,
      type: prev.type.includes(value)
        ? prev.type.filter((id) => id !== value)
        : [...prev.type, value],
    }));
  }, []);

  //  * Remove item de arrays multi-select
  const removeItem = useCallback((item, field) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((i) => {
        if (field === 'criminal_capitulation') {
          return String(i?.id || i) !== String(item?.id || item);
        }
        return i !== item;
      })
    }));
  }, []);

  const handleSelectCapitulation = useCallback((item) => {
    const label = getCriminalCapitulationLabel(item);
    const id = item?.id || '';

    if (!label) return;

    setFormData((prev) => {
      const exists = prev.criminal_capitulation.some((selected) => String(selected?.id || '') === String(id));
      return {
        ...prev,
        criminal_capitulation: exists ? prev.criminal_capitulation : [...prev.criminal_capitulation, { id, label }]
      };
    });

    setSearchState((prev) => ({ ...prev, searchCriminalCapitulation: '' }));
    setLists((prev) => ({ ...prev, criminalCapitulationResults: [] }));
  }, []);

  const handleAddCapitulation = useCallback(() => {
    if (lists.criminalCapitulationResults.length > 0) {
      handleSelectCapitulation(lists.criminalCapitulationResults[0]);
    }
  }, [handleSelectCapitulation, lists.criminalCapitulationResults]);

  const handleSelectCourt = useCallback((court) => {
    setFormData((prev) => ({
      ...prev,
      court: court?.unit || court?.name || '',
      court_id: court?.id || '',
    }));
    setProcessSearchUi((prev) => ({ ...prev, showCourtSuggestions: false }));
  }, []);

  const handleSelectDistrict = useCallback((district) => {
    setFormData((prev) => ({
      ...prev,
      judicial_district: district,
      court: '',
      court_id: '',
    }));
    setProcessSearchUi((prev) => ({ ...prev, showDistrictSuggestions: false }));
    setLists((prev) => ({ ...prev, courtResults: [] }));
  }, []);

  const handleCapitulationKeyDown = useCallback((e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    handleAddCapitulation();
  }, [handleAddCapitulation]);
  //  * Handler de submit do formulário (inclui lógica de upload de arquivo)
  const handleSubmit = async (e) => {
    e.preventDefault();
    await runWithSubmitLock(async () => {
      const shouldSendYears = isCondenatoria;
      const shouldSendMonths = isCondenatoria;
      const shouldSendDays = !isPreventiva && (isCondenatoria || isTemporaria || isCivil);

      const originalCourtLabel = String(
        originalProcessRef.current?.court?.unit
        || originalProcessRef.current?.court?.name
        || originalProcessRef.current?.court
        || ''
      );
      const currentCourtLabel = String(formData.court || '');
      const courtChanged = normalizeSearchText(currentCourtLabel) !== normalizeSearchText(originalCourtLabel);

      const originalCapLabels = Array.isArray(originalProcessRef.current?.criminal_capitulation)
        ? originalProcessRef.current.criminal_capitulation.map((item) => item?.label || item).filter(Boolean).sort().join('|')
        : '';
      const currentCapLabels = Array.isArray(formData.criminal_capitulation)
        ? formData.criminal_capitulation.map((item) => item?.label || item).filter(Boolean).sort().join('|')
        : '';
      const criminalCapChanged = currentCapLabels !== originalCapLabels;

      if (!formData.court_id && (!isEditMode || courtChanged)) {
        toast.warning('Selecione uma vara na lista.');
        return;
      }

      if ((formData.criminal_capitulation || []).some((item) => !item?.id) && (!isEditMode || criminalCapChanged)) {
        toast.warning('Selecione a capitulação penal pela lista.');
        return;
      }

      const payloadJson = {
        number: formData.number || '',
        court: formData.court_id || '',
        judicial_district: formData.judicial_district || '',
        criminal_capitulation: Array.isArray(formData.criminal_capitulation)
          ? formData.criminal_capitulation.map((item) => item?.id).filter(Boolean)
          : [],
        nature_of_prison: formData.nature_of_prison?.id ?? formData.nature_of_prison ?? '',
        origin: formData.origin?.id ?? formData.origin ?? '',
        prison_data: formData.prison_data || '',
        regime: formData.regime || '',
        execution: formData.execution || null,
        extended: !!formData.extended,
        is_active: !!formData.is_active,
        internal: formData.internal || internalId || ''
      };

      if (shouldSendYears) payloadJson.years = formData.years || '';
      if (shouldSendMonths) payloadJson.months = formData.months || '';
      if (shouldSendDays) payloadJson.days = formData.days || '';

      const hasFile = formData.file instanceof File;

      const normalizedFileTypes = [...new Set(
        (formData.type || [])
          .map((type) => String(type).trim())
          .filter(Boolean)
      )];
      const trimmedFileDescription = String(formData.description || '').trim();

      if (isEditMode && hasFile) {
        if (normalizedFileTypes.length === 0) {
          toast.warning('Selecione ao menos um tipo de documento para o novo arquivo.');
          return;
        }

        if (!trimmedFileDescription) {
          toast.warning('Informe a descrição do novo arquivo.');
          return;
        }
      }

      const computeChangedPayload = () => {
        if (!isEditMode || !originalProcessRef.current) return payloadJson;
        const original = originalProcessRef.current;
        const changed = {};

        const currentCap = Array.isArray(payloadJson.criminal_capitulation)
          ? payloadJson.criminal_capitulation.map(String).sort().join(',')
          : '';
        const originalCap = Array.isArray(original.criminal_capitulation)
          ? original.criminal_capitulation.map((item) => String(item?.id || '')).filter(Boolean).sort().join(',')
          : '';
        if (currentCap !== originalCap) changed.criminal_capitulation = payloadJson.criminal_capitulation;

        if ((payloadJson.number || '') !== (original.number || '')) changed.number = payloadJson.number;
        if (String(payloadJson.court || '') !== String(original.court_id || '')) changed.court = payloadJson.court;
        if ((payloadJson.judicial_district || '') !== (original.judicial_district || '')) changed.judicial_district = payloadJson.judicial_district;
        if (String(payloadJson.nature_of_prison || '') !== String(original.nature_of_prison || '')) changed.nature_of_prison = payloadJson.nature_of_prison;
        if (String(payloadJson.origin || '') !== String(original.origin || '')) changed.origin = payloadJson.origin;
        if ((payloadJson.prison_data || '') !== (original.prison_data || '')) changed.prison_data = payloadJson.prison_data;
        if ((payloadJson.regime || '') !== (original.regime || '')) changed.regime = payloadJson.regime;
        if ((payloadJson.execution || '') !== (original.execution || '')) changed.execution = payloadJson.execution;

        if (shouldSendYears && (payloadJson.years || '') !== (original.years || '')) changed.years = payloadJson.years;
        if (shouldSendMonths && (payloadJson.months || '') !== (original.months || '')) changed.months = payloadJson.months;
        if (shouldSendDays && (payloadJson.days || '') !== (original.days || '')) changed.days = payloadJson.days;
        if ((payloadJson.regime || '') !== (original.regime || '')) changed.regime = payloadJson.regime;
        if ((payloadJson.execution || '') !== (original.execution || '')) changed.execution = payloadJson.execution;
        if (!!payloadJson.extended !== !!original.extended) changed.extended = payloadJson.extended;
        if (!!payloadJson.is_active !== !!original.is_active) changed.is_active = payloadJson.is_active;

        return changed;
      };

      let payloadToSend = payloadJson;
      const changedPayload = computeChangedPayload();
      if (isEditMode) {
        payloadToSend = changedPayload;
      }

      if (!isEditMode || hasFile) {
        const data = new FormData();
        const source = isEditMode ? changedPayload : payloadJson;

        Object.keys(source).forEach((key) => {
          if (source[key] === null || source[key] === undefined || source[key] === '') return;

          if (key === 'criminal_capitulation') {
            data.append('criminal_capitulation', JSON.stringify(source.criminal_capitulation || []));
            return;
          }

          if (key === 'criminal_nature') {
            (source.criminal_nature || []).forEach((id) => data.append('criminal_nature', String(id)));
            return;
          }

          data.append(key, source[key]);
        });

        if (hasFile) {
          data.append('file', formData.file);

          if (isEditMode) {
            normalizedFileTypes.forEach((type) => data.append('type', type));

            if (normalizedFileTypes.length === 1) {
              data.append('type', normalizedFileTypes[0]);
            }

            data.append('description', trimmedFileDescription);
          }
        }

        payloadToSend = data;
      }

      try {
        if (isEditMode) {
          await updateProcess(processId, payloadToSend);
        } else {
          await addProcess(payloadToSend);
        }
        setFormData(initialFormData(internalId));
        onSaveSuccess();
        closeModal();
      } catch (error) {
        console.error("Erro ao salvar processo:", error);
        let apiMessage = 'Erro ao processar solicitação';
        if (error.response?.data) {
          const data = error.response.data;
          if (data.message) {
            apiMessage = data.message;
          } else if (data.detail) {
            apiMessage = data.detail;
          } else if (typeof data === 'object') {
            // Captura erros específicos de campos enviados via FormData/JSON
            const messages = Object.entries(data).map(([key, val]) => {
              const fieldName = key.replace('_', ' ').toUpperCase();
              const message = Array.isArray(val) ? val.join(', ') : val;
              return `${fieldName}: ${message}`;
            });
            if (messages.length > 0) apiMessage = messages.join(' | ');
          }
        }
        toast.error(apiMessage);
      }
    });
  };

  return (
    <Modal
      title={readOnly ? 'Visualizar Processo' : isEditMode ? 'Editar Processo' : 'Adicionar Processo'}
      isOpen={isOpen}
      closeModal={closeModal}
    >
      {loadingState.processData ? (
        <div className="empty-state-message"><p>Carregando dados do processo...</p></div>
      ) : (
      <form onSubmit={handleSubmit}>
        {readOnly ? (
          <div className='form-group'>
            <label>Status do processo</label>
            <p className={`process-status-label ${formData.is_active ? 'process-status-active' : 'process-status-inactive'}`}>
              {formData.is_active ? 'Ativo' : 'Inativo'}
            </p>
          </div>
        ) : (
          <div className={`process-active-toggle ${formData.is_active ? 'process-active-toggle--active' : 'process-active-toggle--inactive'}`}>
            <label className="checkbox-label-process process-active-toggle__label">
              <input
                type="checkbox"
                name="is_active"
                checked={!!formData.is_active}
                onChange={handleChange}
              />
              <span className="process-active-toggle__status">
                {formData.is_active ? 'Processo ativo' : 'Processo inativo'}
              </span>
            </label>
            <small className="process-active-toggle__hint">
              {formData.is_active
                ? 'Desmarque para inativar este processo.'
                : 'Marque para reativar este processo.'}
            </small>
          </div>
        )}

        <div className='form-group'>
          <label>Número</label>
          <input
            type="text"
            name="number"
            value={formData.number || ''}
            onChange={handleChange}
            required
            disabled={readOnly}
          />
        </div>
        <div className='form-group'>
          <label>Comarca</label>
          <div className="form-field">
            <input
              type="text"
              name="judicial_district"
              value={formData.judicial_district || ''}
              onFocus={() => setProcessSearchUi((prev) => ({ ...prev, showDistrictSuggestions: true }))}
              onBlur={() => {
                setTimeout(() => {
                  setProcessSearchUi((prev) => ({ ...prev, showDistrictSuggestions: false }));
                }, 150);
              }}
              onChange={(e) => {
                const value = e.target.value;
                handleSelectDistrict(value);
                setProcessSearchUi((prev) => ({ ...prev, showDistrictSuggestions: true }));
              }}
              required
              disabled={readOnly}
              placeholder="Digite para buscar a comarca"
              autoComplete="off"
            />
            {processSearchUi.showDistrictSuggestions && filteredDistrictSuggestions.length > 0 && !readOnly && (
              <ul className="suggestions-list-cities process-field-suggestions">
                {filteredDistrictSuggestions.map((comarca) => (
                  <li key={comarca} onClick={() => handleSelectDistrict(comarca)}>
                    {comarca}
                  </li>
                ))}
              </ul>
            )}
            {loadingState.judicialDistricts && (
              <span className="process-search-loading">Carregando comarcas...</span>
            )}
          </div>
        </div>
        <div className='form-group'>
          <label>Vara</label>
          <div className="form-field">
            <input
              type="text"
              name="court"
              value={formData.court || ''}
              onFocus={() => setProcessSearchUi((prev) => ({ ...prev, showCourtSuggestions: true }))}
              onBlur={() => {
                setTimeout(() => {
                  setProcessSearchUi((prev) => ({ ...prev, showCourtSuggestions: false }));
                }, 150);
              }}
              onChange={(e) => {
                const value = e.target.value;
                setFormData((prev) => ({
                  ...prev,
                  court: value,
                  court_id: '',
                }));
                setProcessSearchUi((prev) => ({ ...prev, showCourtSuggestions: true }));
              }}
              required
              disabled={readOnly || !formData.judicial_district}
              placeholder={formData.judicial_district ? 'Digite para buscar a vara' : 'Selecione a comarca primeiro'}
              autoComplete="off"
            />
            {processSearchUi.showCourtSuggestions && filteredCourtSuggestions.length > 0 && !readOnly && (
              <ul className="suggestions-list-cities process-field-suggestions">
                {filteredCourtSuggestions.map((court) => (
                  <li key={court} onClick={() => {
                    const selectedCourt = lists.courtResults.find((item) => {
                      const label = item?.unit || item?.unidade || item?.court || item?.vara || item?.name || '';
                      return String(label) === String(court);
                    });
                    handleSelectCourt(selectedCourt || { unit: court, id: '' });
                  }}>
                    {court}
                  </li>
                ))}
              </ul>
            )}
            {loadingState.judicialDistricts && (
              <span className="process-search-loading">Carregando varas...</span>
            )}
          </div>
        </div>
        <div className='form-group'>
          <label>Procedência da Prisão</label>
          <select
            name="origin"
            value={String(formData.origin || '')}
            onChange={handleChange}
            required
            disabled={readOnly}
          >
            <option value="">Selecione</option>
            {lists.origins.map(o => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </div>

        {/* Capitulação Penal (busca por artigo/tipificação) */}
        <div className='process-search-container'>
          <label>
            Capitulação Penal
            <TooltipInfo text="Digite artigo ou tipificação para buscar e selecionar." />
          </label>
          <div className="process-capitulation-input">
            <input
              type="text"
              placeholder="Digite artigo ou tipificação..."
              value={searchState.searchCriminalCapitulation}
              onKeyDown={handleCapitulationKeyDown}
              onChange={e =>
                setSearchState(prev => ({
                  ...prev,
                  searchCriminalCapitulation: normalizeSearchText(e.target.value)
                }))
              }
            />
            {lists.criminalCapitulationResults.length > 0 && (
              <div className="process-search-results process-search-results-capitulation">
                {lists.criminalCapitulationResults.map((item, index) => {
                  const label = getCriminalCapitulationLabel(item);
                  const key = `${item?.id || item?.capitulation || item?.art || 'no-art'}-${index}`;
                  return (
                    <div key={key} onClick={() => handleSelectCapitulation(item)}>
                      {label}
                    </div>
                  );
                })}
              </div>
            )}
            {loadingState.criminalCapitulation && (
              <span className="process-search-loading">Buscando capitulação...</span>
            )}
          </div>
          <div className="discipline-selected" style={{ marginBottom: '30px' }}>
            {formData.criminal_capitulation.map((item, idx) => (
              <span
                key={`${item?.id || item?.label || item}-${idx}`}
                onClick={() => removeItem(item, 'criminal_capitulation')}
                className="discipline-selected-item"
              >
                {item?.label || item} ✕
              </span>
            ))}
          </div>
        </div>

        <div className='form-group'>
          <label>Data da Prisão</label>
          <input
            type="date"
            name="prison_data"
            value={formData.prison_data || ''}
            onChange={handleChange}
            required
            disabled={readOnly}
          />
        </div>

        <div className="form-row">
          <div className='form-group form-field-half'>
            <label>Regime</label>
            <select
              name="regime"
              value={String(formData.regime || '')}
              onChange={handleChange}
              required
              disabled={readOnly || loadingState.regimes}
            >
              <option value="">Selecione</option>
              {lists.regimes.map((regime) => (
                <option key={regime.id} value={regime.id}>
                  {regime.name || regime.description || regime.regime || regime.title}
                </option>
              ))}
            </select>
          </div>

          <div className='form-group form-field-half'>
            <label>Natureza da Prisão</label>
            <select
              name="nature_of_prison"
              value={String(formData.nature_of_prison || '')}
              onChange={handleChange}
              required
              disabled={readOnly}
            >
              <option value="">Selecione</option>
              {lists.naturesOfPrison.map(n => (
                <option key={n.id} value={n.id}>{n.name}</option>
              ))}
            </select>
          </div>
        </div>

        {!isPreventiva && (
          <div className='form-group'>
            <h3>Sentença/Temporária</h3>
            <div className="form-row">
              {isCondenatoria && (
                <>
                  <div className="form-field-small">
                    <label>Anos</label>
                    <input
                      type="number"
                      name="years"
                      value={formData.years || ''}
                      onChange={handleChange}
                  required
                      disabled={readOnly}
                    />
                  </div>
                  <div className="form-field-small">
                    <label>Meses</label>
                    <input
                      type="number"
                      name="months"
                      value={formData.months || ''}
                      onChange={handleChange}
                  required
                      disabled={readOnly}
                    />
                  </div>
                </>
              )}
              <div className="form-field-small">
                <label>Dias</label>
                <input
                  type="number"
                  name="days"
                  value={formData.days || ''}
                  onChange={handleChange}
                  required
                  disabled={readOnly}
                />
              </div>
            </div>

            <label className="checkbox-label-process">
              <input
                type="checkbox"
                name="extended"
                checked={!!formData.extended}
                onChange={handleChange}
                disabled={readOnly}
              />
              Processo estendido
            </label>
          </div>

        )}

        <div className='form-group'>
          <label>Vincular a Execução Penal</label>
          <select
            name="execution"
            value={String(formData.execution || '')}
            onChange={handleChange}
            disabled={readOnly}
          >
            <option value="">-- Nenhuma / Criar depois --</option>
            {lists.penalExecutions.map(exec => (
              <option key={exec.id} value={exec.id}>
                {exec.nu || `Exec #${exec.id}`} - {exec.current_regime_name || 'Sem regime'}
              </option>
            ))}
          </select>
        </div>

        {!readOnly && (
          <>
            <div className='form-group modal-form'>
              <label>{isEditMode ? 'Novo documento Relacionado ao processo (opcional)' : 'Documento'}</label>
              <input
                type="file"
                name="file"
                accept='application/pdf'
                onChange={handleChange}
                required={!isEditMode}
              />
            </div>

            {isEditMode && formData.file && (
              <>
                <div className='form-group modal-form'>
                  <label>Descrição do novo arquivo</label>
                  <input
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder='Descrição'
                    required
                  />
                </div>

                <div className='form-group modal-form'>
                  <label>Tipo do novo arquivo</label>
                  <div className="add-document-type-list" role="group" aria-label="Tipos de documento do novo arquivo">
                    {lists.fileTypes.length === 0 ? (
                      <p className="add-document-type-empty">Nenhum tipo de documento disponível.</p>
                    ) : (
                      lists.fileTypes.map((type) => (
                        <label key={type.id} className="add-document-type-option">
                          <input
                            type="checkbox"
                            checked={formData.type.includes(String(type.id))}
                            onChange={() => handleFileTypeToggle(type.id)}
                          />
                          <span>{type.type}</span>
                        </label>
                      ))
                    )}
                  </div>
                  <small className="add-document-type-help">Selecione um ou mais tipos.</small>
                </div>
              </>
            )}
          </>
        )}
        {!readOnly && (
          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Salvando...' : isEditMode ? 'Salvar Alterações' : 'Adicionar Processo'}
          </button>
        )}
      </form>
      )}
    </Modal>
  );
}

export default Process;
